import { CheckboxGroup, Checkbox } from "@nextui-org/checkbox";
import Input from "../../components/Input";
import StepLayout from "../../components/StepLayout";
import { SQFForm, SQFStepOf } from "../../hooks/useServiceQuoteFlow";
import { useStore } from "@tanstack/react-form";
import { formatPhone } from "@/utils/string/format-phone";
import { isMobile } from "@/utils/is-mobile";
import { onEnter } from "@/utils/keyboard/events";
import { cn } from "@networx/utils/tailwind/cn";

export default function HomeownerPhoneStep({ step, form, tcpaText, onClickPrev }: Props) {
  // this is for isDisabled for the next button
  const isSubmitting = useStore(form.store, (state) => state.isSubmitting);
  // this is for the canContinue
  const details = useStore(form.store, (state) => ({
    phone: state.values.phone,
    contractors: state.values.selectedContractors,
  }));
  if (step?.hidden) return null;
  return (
    <StepLayout
      title={step.title}
      subtitle=" "
      onClickPrev={onClickPrev}
      buttonProps={{
        onClick: step.next,
        isDisabled: !step.canContinue(details),
        isLoading: isSubmitting,
        label: isSubmitting ? "Getting Quotes..." : "Get Free Quotes",
        className: ["sm:w-[420px]", isSubmitting && "opacity-50"],
      }}
      fullWidthNextButton
      classNames={{
        cardBase: "h-[794px] justify-start",
        headerWrapper: "sm:px-[27px]",
      }}
    >
      <div className="flex flex-col gap-4 w-full max-w-[420px]">
        <div className={cn("flex flex-col gap-2 mb-4 w-full", { "items-center": isMobile() })}>
          {[
            {
              name: "phone", props: {
                placeholder: "Phone",
                type: "tel",
                formatter: (v?: string) => formatPhone(v ?? ""),
              }
            } as const,
          ].filter(Boolean).map(({ name, props }) => {
            return <form.Field
              key={name}
              name={name}
              validators={step.fields[name].validators}
              listeners={{
                onChange: () => {
                  if (step.autoForward) {
                    step.resolve();
                  }
                },
                onBlur: () => {
                  if (isMobile()) {
                    document.body.scrollIntoView({ behavior: "smooth", block: "start", inline: "nearest" });
                  }
                }
              }}
              children={(field) => {
                const inputProps = step.fields[name].props(field);
                return (
                  <Input
                    {...inputProps}
                    {...props}
                    showIcon={!isMobile() && inputProps.showIcon}
                    autoComplete="tel-national"
                    variant="bordered"
                    errorPreset={isMobile() ? "solid" : "float"}
                    // @ts-expect-error type issue, but it's working
                    onKeyDown={onEnter((e) => {
                      (e.target as HTMLInputElement | undefined)?.blur?.();
                      step.next();
                    })}
                    classNames={{
                      base: cn({ "max-w-[320px]": isMobile() }, "sm:w-[420px]"),
                      inputWrapper: "py-0 h-[42px] rounded-[4px] sm:rounded-[3px] border-1 border-[color:rgba(67,91,108,0.43)] sm:border-[color:rgb(203,195,180)] !shadow-[inset_0_1px_2px_rgba(0,0,0,0.2)] sm:!shadow-none",
                      input: "text-[#666] sm:text-black text-[16px] placeholder:text-[18px]",
                    }}
                  />
                )
              }
              }
            />
          }
          )}
        </div>

        <div className="flex flex-col w-full gap-2 px-4 sm:px-0">
          <form.Field
            name="selectedContractors"
            validators={step.fields.selectedContractors.validators}
            children={(field) => {
              const contractors = field.state.value;
              const checkboxProps = step.fields.selectedContractors.props(field);
              return contractors.length > 0 && (
                <div className="text-xs flex flex-col gap-1 font-display2">
                  <CheckboxGroup
                    className="flex flex-col gap-1"
                    classNames={{
                      label: "text-[12px] text-default-700",
                      wrapper: "gap-0.5",
                    }}
                    {...checkboxProps}
                    value={checkboxProps.value}
                    onValueChange={(v) => {
                      checkboxProps.onValueChange(v);
                      if (step.autoForward) {
                        step.resolve();
                      }
                    }}
                  >
                    {contractors.map(c =>
                      <Checkbox
                        key={c.id}
                        value={c.id}
                        translate="no"
                        classNames={{
                          wrapper: "after:bg-[color:rgb(0,136,255)] before:rounded-small after:rounded-small rounded-small",
                          label: "text-[12px] text-default-700",
                        }}
                      >
                        {c.name}
                      </Checkbox>
                    )}
                  </CheckboxGroup>
                </div>
              )
            }}
          />

        </div>
        {/* TCPA */}
        <div className="text-default-700 font-display2 text-[12px] sm:text-[10px] leading-4" dangerouslySetInnerHTML={{ __html: tcpaText }} />
      </div>
    </StepLayout>
  );
}


interface Props {
  step: SQFStepOf<"homeowner-phone">;
  form: SQFForm;
  tcpaText: string;
  onClickPrev?: () => void;
}
